import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDate } from '../../../config/appConfig';
import { useAccDocuments, type AccDocument } from '../../accounting/documents/useData';
import GenericListWidget from './GenericListWidget';

const journalHref = (doc: AccDocument) =>
  doc.status === 'SAVED' ? `/accounting/journals/${doc.id}` : `/accounting/journals/${doc.id}/edit`;

const journalLabel = (doc: AccDocument) =>
  doc.code?.trim() || doc.description?.trim() || `Journal #${doc.id}`;

const FinanceRecentJournals: React.FC<{
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
}> = ({ limit = 5, embedded, settings }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const { data, isLoading } = useAccDocuments(undefined, 1, 50, {
    sortKey: 'updatedAt',
    sortDirection: 'desc',
  });

  const items = (data ?? []).slice(0, effectiveLimit);

  return (
    <GenericListWidget<AccDocument>
      title="Finance · Recent journals"
      viewAllPath="/accounting/journals"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No journals"
      getItemKey={(d) => d.id}
      embedded={embedded}
      renderItem={(doc) => (
        <div
          onClick={() => navigate(journalHref(doc))}
          className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${colors.text}`}>{journalLabel(doc)}</p>
            <p className={`text-xs ${colors.textMuted}`}>{doc.status}</p>
          </div>
          <span className={`text-xs whitespace-nowrap ${colors.textMuted}`}>
            {formatDate(doc.postingDate || doc.updatedAt || doc.createdAt)}
          </span>
        </div>
      )}
    />
  );
};

export default FinanceRecentJournals;
